import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, ArrowDownRight, Minus, WarningOctagon, Lock,
  Crosshair, Lightning, Target, Pulse,
} from "@phosphor-icons/react";
import AppShell, { SSEContext } from "@/components/AppShell";
import ModeToggle from "@/components/ModeToggle";
import MarketChart from "@/components/MarketChart";
import { ModeContext } from "@/contexts/ModeContext";
import { fetchInstrument, fmtPrice, fmtCountdown, fmtRelative } from "@/lib/api";
import { MOTION_FADE_UP } from "@/lib/uiConstants";
import { AUREUS } from "@/constants/testIds";

// --------------------------------------------------------------------------
// Top strip (Session / Countdown / Kill Zone / Risk / Score / Bias)
// --------------------------------------------------------------------------
function TopStrip({ data }) {
  const s = data.session;
  const sig = data.signal;
  const BiasIcon = sig.bias === "BULLISH" ? ArrowUpRight : sig.bias === "BEARISH" ? ArrowDownRight : Minus;
  const biasCls = sig.bias === "BULLISH" ? "chip-bull" : sig.bias === "BEARISH" ? "chip-bear" : "chip-neutral";
  const riskCls =
    sig.risk_level === "LOW" ? "chip-bull" :
    sig.risk_level === "MEDIUM" ? "chip-gold" : "chip-bear";

  return (
    <div
      data-testid={AUREUS.topStrip}
      className="surface px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3"
    >
      <div className="flex items-center gap-2" data-testid={AUREUS.topStripSession}>
        <span className="overline">Session</span>
        <span className="mono text-sm">{s.active?.label}</span>
      </div>
      <div className="flex items-center gap-2" data-testid={AUREUS.topStripCountdown}>
        <span className="overline">Next</span>
        <span className="mono text-sm">{s.next?.label} · {fmtCountdown(s.next?.starts_in_seconds)}</span>
      </div>
      <div className="flex items-center gap-2" data-testid={AUREUS.topStripKillzone}>
        <span className="overline">Kill Zone</span>
        {s.kill_zone_active
          ? <span className="chip chip-gold">LIVE</span>
          : <span className="chip">DORMANT</span>}
      </div>
      <div className="flex items-center gap-2" data-testid={AUREUS.topStripRisk}>
        <span className="overline">Risk</span>
        <span className={`chip ${riskCls}`}>{sig.risk_level}</span>
      </div>
      <div className="flex items-center gap-2" data-testid={AUREUS.topStripScore}>
        <span className="overline">Score</span>
        <span className="mono text-sm"><span className="text-white">{sig.score}</span><span className="text-zinc-500">/100</span></span>
      </div>
      <div className="flex items-center gap-2 ml-auto" data-testid={AUREUS.topStripBias}>
        <span className="overline">Bias</span>
        <span className={`chip ${biasCls}`}><BiasIcon size={11} weight="bold" /> {sig.bias}</span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Bullish/Bearish Pressure card
// --------------------------------------------------------------------------
function PressureCard({ pressure }) {
  return (
    <div data-testid={AUREUS.pressureCard} className="surface p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Pressure · Bullish vs Bearish</div>
        <Pulse size={16} className="text-zinc-500" />
      </div>
      <div className="h-3 w-full bg-bear/15 rounded-sm overflow-hidden flex">
        <div
          data-testid={AUREUS.pressureBullish}
          className="bg-bull/70 h-full transition-all duration-700"
          style={{ width: `${pressure.bullish}%` }}
        />
        <div
          data-testid={AUREUS.pressureBearish}
          className="bg-bear/70 h-full transition-all duration-700"
          style={{ width: `${pressure.bearish}%` }}
        />
      </div>
      <div className="mt-3 flex justify-between mono text-xs">
        <span className="text-bull">▲ Bullish {pressure.bullish.toFixed(1)}%</span>
        <span className="text-bear">▼ Bearish {pressure.bearish.toFixed(1)}%</span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// TRADE BRIEF — hero glass card
// --------------------------------------------------------------------------
function LivePrice({ symbol, restPrice }) {
  const { tickMap } = useContext(SSEContext);
  const tick = tickMap[symbol];
  const price = tick?.price ?? restPrice;
  const direction = tick?.direction ?? "flat";
  const ref = useRef(null);
  const lastDir = useRef("flat");

  useEffect(() => {
    if (!ref.current) return;
    if (direction !== lastDir.current && direction !== "flat") {
      ref.current.classList.remove("tick-up", "tick-down");
      void ref.current.offsetWidth;
      ref.current.classList.add(direction === "up" ? "tick-up" : "tick-down");
      lastDir.current = direction;
    }
  }, [direction, price]);

  return (
    <div className="flex items-baseline gap-3">
      <span
        ref={ref}
        data-testid={AUREUS.tbPrice}
        className="heading text-5xl md:text-6xl font-extrabold tracking-tighter mono px-1"
      >
        {fmtPrice(price)}
      </span>
      <span className="overline">live</span>
    </div>
  );
}

function ZoneCell({ overline, primary, secondary, accent = "white", testId }) {
  const color =
    accent === "bull" ? "text-bull" :
    accent === "bear" ? "text-bear" :
    accent === "gold" ? "text-gold" : "text-white";
  return (
    <div data-testid={testId} className="p-5 border-t border-white/10 md:border-t-0 md:border-l first:md:border-l-0">
      <div className="overline mb-3">{overline}</div>
      <div className={`mono text-xl md:text-2xl font-semibold tracking-tighter ${color}`}>
        {primary}
      </div>
      {secondary && <div className="mono text-xs text-zinc-500 mt-1">{secondary}</div>}
    </div>
  );
}

function TradeBrief({ data }) {
  const { signal, symbol, price } = data;
  const tb = signal.trade_brief;
  const isLocked = !!tb.locked;
  const tp1Hit = !!tb.tp1_hit;
  const isBullish = signal.bias === "BULLISH";
  const isBearish = signal.bias === "BEARISH";
  const directionLabel = isBullish ? "BUY" : isBearish ? "SELL" : "WAIT";
  const directionClass = isBullish ? "chip-bull" : isBearish ? "chip-bear" : "chip-neutral";
  const DirectionIcon = isBullish ? ArrowUpRight : isBearish ? ArrowDownRight : Minus;
  const tierColor =
    signal.effective_tier === "A" ? "chip-gold" :
    signal.effective_tier === "B" ? "chip-neutral" : "chip-bear";
  return (
    <motion.div
      data-testid={AUREUS.tradeBrief}
      {...MOTION_FADE_UP}
      className="glass relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none gold-aurora opacity-30" />
      <div className="relative p-6 md:p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="overline">Trade Brief · {symbol}</div>
              <span
                data-testid="tb-direction"
                className={`chip ${directionClass} font-bold tracking-widest`}
              >
                <DirectionIcon size={12} weight="bold" />
                {directionLabel} · {signal.bias}
              </span>
              {isLocked ? (
                <span data-testid="tb-locked" className="chip chip-gold">
                  <Lock size={11} weight="fill" />
                  LOCKED · {fmtRelative(tb.locked_at)}
                </span>
              ) : (
                <span className="chip chip-neutral">PREVIEW · awaiting setup</span>
              )}
              {tp1Hit && (
                <span className="chip chip-bull">TP1 HIT · trailing</span>
              )}
            </div>
            <LivePrice symbol={symbol} restPrice={price} />
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span data-testid={AUREUS.tbTier} className={`chip ${tierColor}`}>
                TIER {signal.effective_tier}
              </span>
              {signal.auto_downgraded && (
                <span data-testid={AUREUS.tbDowngrade} className="chip chip-bear">
                  <WarningOctagon size={11} weight="fill" />
                  Auto-Downgraded
                </span>
              )}
            </div>
            <div className="mono text-xs text-zinc-500">
              {isLocked
                ? <>setup status <span className="text-white">{tb.setup_status}</span></>
                : <>base tier <span className="text-white">{signal.tier}</span></>
              }
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 border border-white/10 bg-black/30">
          <ZoneCell
            overline="Entry Zone"
            testId={AUREUS.tbEntry}
            accent="gold"
            primary={`${fmtPrice(tb.entry_zone.low)} – ${fmtPrice(tb.entry_zone.high)}`}
            secondary={isLocked ? "Locked optimal fill" : "Optimal fill window"}
          />
          <ZoneCell
            overline="Risk Zone (SL)"
            testId={AUREUS.tbRisk}
            accent="bear"
            primary={`${fmtPrice(tb.risk_zone.low)} – ${fmtPrice(tb.risk_zone.high)}`}
            secondary={isLocked ? "Locked invalidation" : "Invalidation band"}
          />
          <ZoneCell
            overline="Target 1"
            testId={AUREUS.tbT1}
            accent="bull"
            primary={fmtPrice(tb.target_1.price)}
            secondary={`RR ${tb.rr_t1.toFixed(1)} · ${tb.target_1.label}`}
          />
          <ZoneCell
            overline="Target 2"
            testId={AUREUS.tbT2}
            accent="bull"
            primary={fmtPrice(tb.target_2.price)}
            secondary={`RR ${tb.rr_t2.toFixed(1)} · ${tb.target_2.label}`}
          />
        </div>

        <p
          data-testid={AUREUS.tbWhy}
          className="mt-6 pt-6 border-t border-white/10 text-sm text-zinc-300 leading-relaxed max-w-4xl"
        >
          <span className="overline mr-2">Why</span>
          {data.why}
        </p>
      </div>
    </motion.div>
  );
}

// --------------------------------------------------------------------------
// SMC / ICT Technical Box
// --------------------------------------------------------------------------
function SMCBox({ chips }) {
  const tone = (status) => {
    if (["active", "confirmed"].includes(status)) return "chip-bull";
    if (["discount"].includes(status)) return "chip-bull";
    if (["premium"].includes(status)) return "chip-bear";
    if (["watching", "equilibrium"].includes(status)) return "chip-gold";
    return "chip-neutral";
  };
  return (
    <div data-testid={AUREUS.smcBox} className="surface p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">SMC / ICT · Technical Stack</div>
        <Crosshair size={16} className="text-zinc-500" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {chips.map((c) => (
          <div
            key={c.id}
            data-testid={AUREUS.smcChip(c.id)}
            className="border border-white/10 p-3 bg-black/30 hover:bg-white/5 transition"
          >
            <div className="overline">{c.id}</div>
            <div className="mt-1 text-sm">{c.label}</div>
            <div className="mt-2 flex items-center justify-between">
              <span className={`chip ${tone(c.status)}`}>{String(c.status).toUpperCase()}</span>
              {c.tag && <span className="mono text-[10px] text-zinc-500">{c.tag}</span>}
              {c.direction && (
                <span className={`mono text-[10px] ${c.direction === "up" ? "text-bull" : "text-bear"}`}>
                  {c.direction === "up" ? "▲" : "▼"}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Liquidity Map (vertical ladder)
// --------------------------------------------------------------------------
function LiquidityMap({ levels, currentPrice }) {
  // Sort high → low (matches visual ladder, prices stacked top-down)
  const sorted = useMemo(() => [...levels].sort((a, b) => b.price - a.price), [levels]);
  const max = Math.max(...sorted.map((l) => l.price), currentPrice);
  const min = Math.min(...sorted.map((l) => l.price), currentPrice);
  const span = max - min || 1;
  const yOf = (p) => `${((max - p) / span) * 100}%`;

  return (
    <div data-testid={AUREUS.liquidityMap} className="surface p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Liquidity Map · Resting Pools</div>
        <Target size={16} className="text-zinc-500" />
      </div>
      <div className="relative h-72 border border-white/10 bg-black/30">
        {/* Mid grid */}
        <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-white/5" />
        {/* Levels */}
        {sorted.map((lv) => {
          const isHigh = lv.kind.endsWith("_H");
          return (
            <div
              key={lv.kind}
              data-testid={AUREUS.liquidityLevel(lv.kind)}
              className="absolute left-0 right-0 flex items-center"
              style={{ top: yOf(lv.price) }}
            >
              <div className={`h-px flex-1 ${isHigh ? "bg-bear/40" : "bg-bull/40"}`} />
              <div className="ml-3 flex items-center gap-2 text-[11px]">
                <span className="mono text-zinc-400">{fmtPrice(lv.price)}</span>
                <span className={`chip ${isHigh ? "chip-bear" : "chip-bull"}`}>{lv.label}</span>
              </div>
            </div>
          );
        })}
        {/* Current price marker */}
        <div
          className="absolute left-0 right-0 flex items-center"
          style={{ top: yOf(currentPrice) }}
        >
          <div className="h-0.5 flex-1 bg-gold/80" />
          <div className="ml-3 flex items-center gap-2 text-[11px]">
            <span className="mono text-gold">{fmtPrice(currentPrice)}</span>
            <span className="chip chip-gold">PRICE</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Scenario Map
// --------------------------------------------------------------------------
function ScenarioCard({ scen, accent, testId }) {
  const accentCls = accent === "primary" ? "border-gold/50" : "border-white/10";
  const tag = accent === "primary" ? "chip-gold" : "chip-neutral";
  return (
    <div data-testid={testId} className={`surface p-5 border ${accentCls}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`chip ${tag}`}>{accent === "primary" ? "PRIMARY" : "ALTERNATIVE"}</span>
        <span className="mono text-xs text-zinc-500">conv <span className="text-white">{scen.conviction}</span></span>
      </div>
      <div className="heading text-lg font-semibold tracking-tight">{scen.label}</div>
      <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{scen.summary}</p>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="overline">Target</div>
          <div className="mono text-bull">{fmtPrice(scen.target)}</div>
        </div>
        <div>
          <div className="overline">Invalidation</div>
          <div className="mono text-bear">{fmtPrice(scen.invalidation)}</div>
        </div>
      </div>
    </div>
  );
}

function ScenarioMap({ scenarios }) {
  return (
    <div data-testid={AUREUS.scenarioMap} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ScenarioCard scen={scenarios.primary} accent="primary" testId={AUREUS.scenarioPrimary} />
      <ScenarioCard scen={scenarios.alternative} accent="alt" testId={AUREUS.scenarioAlt} />
    </div>
  );
}

// --------------------------------------------------------------------------
// Sniper + Scalping entry zones
// --------------------------------------------------------------------------
function EntryZoneCard({ kind, zone, Icon, testId }) {
  const isSniper = kind === "sniper";
  const accent = isSniper ? "text-gold" : "text-bull";
  return (
    <div data-testid={testId} className="surface p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon size={16} className={accent} />
          <span className="overline">{isSniper ? "Trade Zone · Structural" : "Short Zone · Scalp"}</span>
        </div>
        <span className={`chip ${isSniper ? "chip-gold" : "chip-bull"}`}>RR {zone.rr.toFixed(1)}</span>
      </div>
      <div className="mono text-2xl font-semibold tracking-tighter">
        {fmtPrice(zone.low)} <span className="text-zinc-600">→</span> {fmtPrice(zone.high)}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="mono text-zinc-500">conviction</span>
        <span className={`chip ${isSniper ? "chip-gold" : "chip-neutral"}`}>{zone.conviction}</span>
      </div>
    </div>
  );
}

function EntryZones({ zones }) {
  return (
    <div data-testid={AUREUS.entryZones} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <EntryZoneCard kind="sniper"   zone={zones.sniper}   Icon={Crosshair} testId={AUREUS.entrySniper} />
      <EntryZoneCard kind="scalping" zone={zones.scalping} Icon={Lightning} testId={AUREUS.entryScalp} />
    </div>
  );
}

// --------------------------------------------------------------------------
// Page
// --------------------------------------------------------------------------
export default function SinglePairPage() {
  const { symbol } = useParams();
  const SYM = (symbol || "XAUUSD").toUpperCase();
  const { mode } = useContext(ModeContext);

  const { data, isLoading, error } = useQuery({
    queryKey: ["instrument", SYM, mode],
    queryFn: () => fetchInstrument(SYM, { mode }),
    refetchInterval: 3000,
  });

  return (
    <AppShell>
      <div className="p-6 md:p-10" data-testid={AUREUS.detailRoot}>
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition">
            <ArrowLeft size={14} /> All markets
          </Link>
          <div className="flex items-center gap-4 flex-wrap">
            <ModeToggle />
            <div className="heading text-3xl md:text-4xl font-bold tracking-tighter">
              {SYM}
              {data?.name && <span className="text-zinc-500 ml-3 text-base mono">· {data.name}</span>}
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="space-y-4">
            <div className="surface h-16 animate-pulse" />
            <div className="surface h-72 animate-pulse" />
            <div className="surface h-40 animate-pulse" />
          </div>
        )}

        {error && (
          <div className="surface p-6 text-bear border-bear/40">
            Failed to load {SYM}: {String(error.message || error)}
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-6">
            <TopStrip data={data} />
            <MarketChart
              symbol={SYM}
              // timeframe={timeframe}
              // onTimeframeChange={setTimeframe}
              levels={data.signal?.trade_brief?.locked ? {
                entry_low:  data.signal.trade_brief.entry_zone?.low,
                entry_high: data.signal.trade_brief.entry_zone?.high,
                sl:         data.signal.trade_brief.risk_zone?.[data.signal.bias === "BEARISH" ? "high" : "low"],
                t1:         data.signal.trade_brief.target_1?.price,
                t2:         data.signal.trade_brief.target_2?.price,
                bias:       data.signal.bias,
              } : null}
            />
            <PressureCard pressure={data.signal.pressure} />
            <TradeBrief data={data} />
            <SMCBox chips={data.smc_ict} />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2">
                <LiquidityMap levels={data.liquidity_map} currentPrice={data.price} />
              </div>
              <div className="lg:col-span-3 flex flex-col gap-6">
                <ScenarioMap scenarios={data.scenarios} />
                <EntryZones zones={data.entry_zones} />
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
